import { useEffect, useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ApiError } from '@/lib/api/errors';
import {
  type NotificationPreferenceKey,
  type NotificationPreferences as NotificationPreferencesValue,
  unrecognizedKeysFromError,
} from '@/lib/api/domains/notification-preferences';
import type { UserId } from '@/lib/api/ids';
import {
  NOTIFICATION_PREFERENCE_LABELS,
  NOTIFICATION_PREFERENCE_ORDER,
} from '../schemas/notification-preferences';
import { notificationPreferencesQuery } from '../api/notification-preference-queries';
import { useUpdateNotificationPreferencesOnBehalf } from '../api/notification-preference-mutations';

/*
 * The ADMINISTRATOR on-behalf surface (F02 R27, AC-7aaa025f): an administrator,
 * from a user's own-organization record, sees and changes that user's four
 * notification choices on their behalf.
 *
 *   - the read is SCOPED by the server to the target user's own organization
 *     (AC-7aaa025f): an out-of-scope target comes back as the byte-identical
 *     not-found (404), which this surface treats as "no such user" — it renders
 *     NO changeable control (no switch, no Save). The frontend never widens the
 *     scope; the server is the authority and this is the courtesy half;
 *   - the write goes through `useUpdateNotificationPreferencesOnBehalf(target)`,
 *     sending ONLY the target id and the choices. WHO acted (the administrator)
 *     is the server's, established from the session (`/me`) — never anything the
 *     browser sends — so the change record names the person who acted
 *     (AC-bf8668ea) and the user's own portal history later reads
 *     "<name> changed it". Mutate → invalidate (no optimistic write);
 *   - the target is addressed by id; the user's NAME is shown in the UI but
 *     never travels in the URL/request (AGENTS.md — ids only, no PII).
 *
 * The four governed keys (AC-09826b9a) render as labelled toggles in the
 * registry's order, seeded from the server's values (default-on already applied
 * on read). An unknown-key refusal (AC-8ddebd3b) surfaces where the admin works,
 * naming the unrecognized keys (AC-c7c5f9af); nothing is shown as saved.
 */
export function AdminNotificationPreferences({
  targetUserId,
  targetUserName,
}: {
  targetUserId: UserId;
  targetUserName: string;
}) {
  const query = useQuery(notificationPreferencesQuery(targetUserId));
  const update = useUpdateNotificationPreferencesOnBehalf(targetUserId);
  const headingId = useId();

  // Local working copy of the toggles, seeded from the server's values and kept
  // in sync when the server values change (first load, and after a save
  // invalidates and re-reads). The server owns the truth.
  const [draft, setDraft] = useState<NotificationPreferencesValue | null>(null);
  const serverValues = query.data;
  useEffect(() => {
    if (serverValues) setDraft(serverValues);
  }, [serverValues]);

  // A scoped not-found (AC-7aaa025f): the server refused the read for an
  // out-of-scope target with the byte-identical 404. Treat it as "no such user"
  // — render no changeable control, exactly as if the account did not exist.
  const notFound =
    query.error instanceof ApiError && query.error.status === 404;

  const unrecognized = update.isError
    ? unrecognizedKeysFromError(update.error)
    : null;

  if (notFound) {
    return (
      <section aria-labelledby={headingId} className="flex flex-col gap-4">
        <h2 id={headingId} className="text-base font-medium">
          Notification preferences
        </h2>
        <p className="text-sm text-muted-foreground">
          This user could not be found.
        </p>
      </section>
    );
  }

  // Split "loading" from "error" (finding 11): a failed read (other than the
  // scoped 404 handled above) must NOT render a permanent "Loading…". Show a
  // retryable error state instead. The 401/403 interceptor is the backstop.
  if (!draft && query.isError) {
    return (
      <section aria-labelledby={headingId} className="flex flex-col gap-3">
        <h2 id={headingId} className="text-base font-medium">
          Notification preferences
        </h2>
        <Alert variant="destructive" role="alert">
          <AlertTitle>Preferences could not be loaded</AlertTitle>
          <AlertDescription>
            Something went wrong. Please try again.
          </AlertDescription>
        </Alert>
        <div>
          <Button
            type="button"
            variant="outline"
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        </div>
      </section>
    );
  }

  if (!draft) {
    return (
      <section aria-labelledby={headingId}>
        <h2 id={headingId} className="text-base font-medium">
          Notification preferences
        </h2>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </section>
    );
  }

  function setKey(key: NotificationPreferenceKey, enabled: boolean) {
    setDraft((prev) => (prev ? { ...prev, [key]: enabled } : prev));
  }

  function onSave() {
    if (!draft) return;
    update.mutate({ choices: draft });
  }

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <h2 id={headingId} className="text-base font-medium">
        Notification preferences
      </h2>
      {/* The target's NAME is shown in the UI; it never travels in the request
          (addressed by id, AGENTS.md — no PII in URLs/telemetry). */}
      <p className="text-sm text-muted-foreground">
        Changing preferences for {targetUserName}.
      </p>

      {update.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Preferences were not saved</AlertTitle>
          <AlertDescription>
            {unrecognized && unrecognized.length > 0
              ? `These choices were not recognized and nothing was saved: ${unrecognized.join(', ')}.`
              : 'Something went wrong and nothing was saved. Please try again.'}
          </AlertDescription>
        </Alert>
      ) : null}

      <ul className="flex flex-col gap-3">
        {NOTIFICATION_PREFERENCE_ORDER.map((key) => {
          const labelId = `${headingId}-${key}`;
          return (
            <li key={key} className="flex items-center justify-between gap-4">
              <Label id={labelId} htmlFor={labelId + '-switch'}>
                {NOTIFICATION_PREFERENCE_LABELS[key]}
              </Label>
              <Switch
                id={labelId + '-switch'}
                aria-labelledby={labelId}
                checked={draft[key]}
                onCheckedChange={(checked) => setKey(key, checked)}
              />
            </li>
          );
        })}
      </ul>

      <div className="flex justify-end">
        <Button type="button" onClick={onSave} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </section>
  );
}
